import { Box, Typography } from '@mui/material';
import he from 'he';
import sanitizeHtml from 'sanitize-html';

interface Faqs {
  faqs: {
    title: string;
    faqList: Faq[];
  };
}

interface Faq {
  question: string;
  answer: string;
}

interface BaseEntity {
  '@type': string;
}

interface Entity extends BaseEntity {
  name: string;
}

interface AcceptedAnswer extends BaseEntity {
  text: string;
}

interface MainEntity extends Entity {
  acceptedAnswer: AcceptedAnswer;
}

interface LdJson extends BaseEntity {
  '@context': string;
  mainEntity: MainEntity[];
}

const FAQs = ({ faqs }: Faqs) => {
  const schemaJson: LdJson = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [],
  };

  const formatJSON = (faq: Faq) => {
    return {
      '@type': 'Question',
      name: `${faq.question}`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${faq.answer}`,
      },
    };
  };

  const createFaqSchema = () => {
    if (faqs.faqList) {
      schemaJson.mainEntity = faqs.faqList.map((faq) => formatJSON(faq));
    }
    return schemaJson;
  };

  return (
    <Box mt={5} mb={{ xs: 5, md: 10 }}>
      <Typography
        variant="h2"
        color="primary"
        textAlign={{ xs: 'center', sm: 'left' }}
        mb={{ xs: 3, md: 4 }}
        fontWeight="400"
        lineHeight="28px">
        {faqs.title}
      </Typography>
      {faqs.faqList &&
        faqs.faqList.map((faq: Faq) => (
          <Box key={faq.question}>
            <Typography variant="h4" color="primary" mb={1.5}>
              {faq.question}
            </Typography>
            <Box
              role="dialog"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(he.decode(faq.answer)) }}
              sx={(theme) => ({
                marginBottom: theme.spacing(3),
                fontSize: '0.875rem',
                fontWeight: 400,
                lineHeight: '20px',
                color: theme.palette.care.navy?.[900],

                [theme.breakpoints.up('sm')]: {
                  fontSize: '1rem',
                  lineHeight: '22px',
                  marginBottom: theme.spacing(4),
                },
              })}
            />
          </Box>
        ))}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(createFaqSchema()) }}
      />
    </Box>
  );
};

export default FAQs;
